import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type {
  Component,
  IModularBenefitDetail,
} from '@ben/shared/types/columbia';
import { useCallback, useState } from 'react';
import type { DropResult } from '@hello-pangea/dnd';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { useSessionStorage } from 'usehooks-ts';
import { generateNewRank } from '../../utils';
import useSetDirtyValue from '../hooks/use-set-dirty-value';
import AddModularComponentsButton from './add-modular-components-button';
import ModularStepComponent from './modular-step-component';
import { toggleAccordion } from './toggleAccordion';

interface IModularStepComponentWrapperProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  componentControllerName: any;
  isSubForm?: boolean;
  subFormType?: string;
}

const ModularStepComponentWrapper = ({
  componentControllerName,
  isSubForm = false,
  subFormType,
}: IModularStepComponentWrapperProps) => {
  const { control, clearErrors } = useFormContext<IModularBenefitDetail>();

  const setDirtyValue = useSetDirtyValue<IModularBenefitDetail>();

  const { fields, append, remove, swap } = useFieldArray({
    control,
    name: componentControllerName,
    keyName: '_id',
  });

  const watchedComponents = useWatch({
    control,
    name: componentControllerName,
    exact: true,
    defaultValue: fields,
  });

  const watchedArray = Array.isArray(watchedComponents)
    ? watchedComponents
    : fields;

  const initialOpenComponents = watchedArray
    .map((component, index) => ({ component, index }))
    .filter(({ component }) => Boolean(component?.importedFromAirtable))
    .map(({ index }) => index);

  const [openComponents, setOpenComponents] = useSessionStorage<number[]>(
    `${componentControllerName}_openComponents`,
    initialOpenComponents
  );

  const [isComponentReordering, setIsComponentReordering] =
    useState<boolean>(false);

  const hasComponents = fields.length > 0;

  const addComponent = useCallback(
    (component: Component) => {
      const newRank =
        generateNewRank(fields as unknown as { rank: string | number }[]) ??
        fields.length;
      const base = {
        ...component,
        rank: newRank,
        name: '',
        key: '',
        required: false,
        showName: true,
        hideForEmployee: false,
        hideForEcc: false,
        windows: [] as unknown[],
      };
      const newComponent =
        component.type === 'MULTI_SELECT' ||
        component.type === 'MULTI_SELECT_WITH_SUBFORM'
          ? {
              ...base,
              defaultValue: null as unknown as string[] | null,
              value: [] as string[],
            }
          : base;

      append(newComponent);

      setOpenComponents((previousOpenComponents) => {
        const newOpenComponents = [...previousOpenComponents];
        newOpenComponents.push(newRank);
        return newOpenComponents;
      });
    },
    [fields, append, setOpenComponents]
  );

  const handleComponentToggle = (componentRank: number) => {
    toggleAccordion(setOpenComponents, componentRank);
  };

  const toDot = (path: string) => path.replaceAll(/\[(\d+)]/g, '.$1');

  const handleComponentDelete = useCallback(
    (componentIdOrIndex: number | string) => {
      const dotArrayPath = toDot(componentControllerName);
      const indexToDelete =
        typeof componentIdOrIndex === 'number'
          ? componentIdOrIndex
          : fields.findIndex(
              (_, index) =>
                String(fields[index]?._id ?? index) ===
                String(componentIdOrIndex)
            );
      if (indexToDelete < 0) return;

      const removedPath = `${dotArrayPath}.${indexToDelete}`;
      const removedIndex = indexToDelete;
      clearErrors(removedPath as keyof IModularBenefitDetail);

      remove(indexToDelete);

      clearErrors(dotArrayPath as keyof IModularBenefitDetail);

      setOpenComponents((previousOpenComponents) =>
        previousOpenComponents.filter((index) => index !== removedIndex)
      );
    },
    [remove, setOpenComponents, clearErrors, componentControllerName, fields]
  );

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      const { source, destination } = result;
      const [sourceIndex, destinationIndex] = [source.index, destination.index];
      if (sourceIndex === destinationIndex) return;
      swap(sourceIndex, destinationIndex);

      const newOpenComponents = [...watchedArray];
      const [removed] = newOpenComponents.splice(sourceIndex, 1);
      newOpenComponents.splice(destinationIndex, 0, removed);

      const rankedReorderedComponents = newOpenComponents.map(
        (component, index) => ({ ...component, rank: index })
      );

      setDirtyValue(componentControllerName, rankedReorderedComponents);
    },
    [componentControllerName, setDirtyValue, watchedArray, swap]
  );

  return (
    <Wrapper>
      <ComponentHeader>
        <Typography variant={TypographyVariant.bodyLg2}>
          {isSubForm ? `Sub-components: ${subFormType}` : 'Step components'}
        </Typography>
        {hasComponents ? (
          <Button
            variant={ButtonVariant.secondary}
            onClick={() => setIsComponentReordering(!isComponentReordering)}
            label={
              isComponentReordering ? 'Exit reordering' : 'Reorder components'
            }
            width="fit-content"
          />
        ) : (
          <AddModularComponentsButton handleAddComponent={addComponent} />
        )}
      </ComponentHeader>
      {hasComponents ? (
        <>
          <StyledDragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {fields.map((_, index) => {
                    const modularComponent = watchedArray[index] ?? {};
                    const keyBase = String(fields[index]?._id ?? index);
                    return (
                      <Draggable
                        key={keyBase}
                        draggableId={keyBase}
                        index={index}
                        isDragDisabled={!isComponentReordering}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            key={keyBase}
                          >
                            <ComponentWrapper
                              key={`${keyBase}${String(modularComponent.type ?? '')}`}
                            >
                              <ModularStepComponent
                                type={modularComponent.type}
                                index={index}
                                isExpanded={openComponents.includes(index)}
                                onToggle={handleComponentToggle}
                                onRemove={handleComponentDelete}
                                componentId={keyBase}
                                componentControllerName={`${componentControllerName}[${index}]`}
                                isImportedFromAirtable={Boolean(
                                  modularComponent.importedFromAirtable
                                )}
                              />
                            </ComponentWrapper>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                </div>
              )}
            </Droppable>
          </StyledDragDropContext>

          <AddComponentButtonWrapper>
            <AddModularComponentsButton
              handleAddComponent={addComponent}
              isSubForm={isSubForm}
            />
          </AddComponentButtonWrapper>
        </>
      ) : (
        <StyledTypography variant={TypographyVariant.bodyLg1}>
          No components have been added yet.
        </StyledTypography>
      )}
    </Wrapper>
  );
};

export default ModularStepComponentWrapper;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
    width: 100%;

    & > [data-rfd-droppable-id='droppable'] {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing(2)};
    }
  `}
`;

const ComponentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ComponentWrapper = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.palette.primary.bg};
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;

const StyledDragDropContext = styled(DragDropContext)`
  ${({ theme }) => css`
    width: 100%;
    background: ${theme.palette.primary.bg};
  `}
`;

const AddComponentButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  width: 100%;
`;

const StyledTypography = styled(Typography)`
  ${({ theme }) => css`
    margin-top: ${theme.spacing(-2)};
  `}
`;
